import classNames from 'classnames'
import { ContentState, EditorState, Modifier, RichUtils } from 'draft-js'
import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { Link45deg } from 'react-bootstrap-icons'
import { AppProps } from '../AppProps'
import { Button } from '../UiComponents/button'

declare interface LinkEditorProps {
    open: boolean
    setOpen: Dispatch<SetStateAction<boolean>>
    editorState: EditorState
    setEditorState: Dispatch<SetStateAction<EditorState>>
}

declare interface LinkEntityProps extends AppProps {
    contentState: ContentState
    entityKey: string
}

declare interface EntityRange {
    start: number
    end: number
    text: string
}

declare interface Link {
    url: string
    title: string
}

declare interface LinkEditorValues {
    link: Link
    selectedText: string
}

// Utility functions to help get the selected link (when a user edits an existing one)
const getSelectedBlocks = (editorState: EditorState) => {
    const selection = editorState.getSelection()
    const content = editorState.getCurrentContent()
    const startKey = selection.getStartKey()
    const endKey = selection.getEndKey()
    const blockMap = content.getBlockMap()
    return blockMap
        .toSeq()
        .skipUntil((_, k) => k === startKey)
        .takeUntil((_, k) => k === endKey)
        .concat([[endKey, blockMap.get(endKey)]])
        .toArray()
}

const getSelectedBlock = (editorState: EditorState) => {
    return getSelectedBlocks(editorState)[0]
}

const getSelectedEntity = (editorState: EditorState): string | undefined => {
    let entity
    const selection = editorState.getSelection()
    let start = selection.getStartOffset()
    let end = selection.getEndOffset()

    if (start === end && start === 0) {
        end = 1
    } else if (start === end) {
        start -= 1
    }

    const selectedBlock = getSelectedBlock(editorState)

    for (let i = start; i < end; i++) {
        const currentEntity = selectedBlock.getEntityAt(i)

        if (!currentEntity) {
            entity = undefined
            break
        }

        if (i === start) {
            entity = currentEntity
        } else if (entity !== currentEntity) {
            entity = undefined
            break
        }
    }

    return entity
}

const getSelectedText = (editorState: EditorState) => {
    let selectedText = ''
    const selection = editorState.getSelection()
    let start = selection.getAnchorOffset()
    let end = selection.getFocusOffset()
    const selectedBlocks = getSelectedBlocks(editorState)

    if (selectedBlocks.length > 0) {
        if (selection.getIsBackward()) {
            const temp = start
            start = end
            end = temp
        }

        for (let i = 0; i < selectedBlocks.length; i++) {
            const blockStart = i === 0 ? start : 0
            const blockEnd = i === selectedBlocks.length - 1 ? end : selectedBlocks[i].getText().length
            selectedText += selectedBlocks[i].getText().slice(blockStart, blockEnd)
        }
    }

    return selectedText
}

const getEntityRange = (editorState: EditorState, entityKey: string): EntityRange | undefined => {
    const selectedBlock = getSelectedBlock(editorState)
    let entityRange
    selectedBlock.findEntityRanges(
        (value) => value.getEntity() === entityKey,
        (start, end) => {
            entityRange = {
                start,
                end,
                text: selectedBlock.getText().slice(start, end),
            }
        }
    )
    return entityRange
}

export function LinkEntity({ contentState, entityKey, children }: LinkEntityProps): JSX.Element {
    const { url } = contentState.getEntity(entityKey).getData()

    return (
        <a className="underline text-usfGreen dark:text-usfGold" href={url} target="_blank" rel="noreferrer">
            {children}
        </a>
    )
}

export function LinkEditor({ open, setOpen, editorState, setEditorState }: LinkEditorProps): JSX.Element {
    const ref: React.MutableRefObject<HTMLDivElement | null> = useRef(null)

    const [linkTitle, setLinkTitle] = useState('')
    const [linkUrl, setLinkUrl] = useState('')
    const [currentEntity, setCurrentEntity] = useState(getSelectedEntity(editorState))

    const [addBtnDisabled, setAddBtnDisabled] = useState(false)
    const [deleteBtnDisabled, setDeleteBtnDisabled] = useState(false)

    useEffect(() => {
        setCurrentEntity(getSelectedEntity(editorState))
    }, [editorState])

    useEffect(() => {
        if (!currentEntity) {
            if (!deleteBtnDisabled) {
                setDeleteBtnDisabled(true)
            }
        } else {
            if (deleteBtnDisabled) {
                setDeleteBtnDisabled(false)
            }
        }
    }, [currentEntity])

    useEffect(() => {
        if (!open) {
            setLinkTitle('')
            setLinkUrl('')
        }
    }, [open])

    const buttonClasses = classNames('outline-none focus:outline-none border px-3 py-1 bg-white rounded-sm flex items-center', 'bg-slate-100 dark:bg-slate-800')

    const dropdownClasses = classNames('bg-slate-100 dark:bg-slate-800 origin-top-right absolute left-0 mt-2 rounded-md shadow-lg min-w-max p-3')

    const linkInputClasses = classNames(
        'block rounded-sm px-1',
        'border border-slate-300 dark:border-slate-800',
        'bg-slate-50 text-slate-900 dark:bg-slate-600 dark:text-slate-100',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-usfGreen'
    )

    const addLinkBtnClasses = classNames(
        'px-2 py-1 rounded-md mr-3 cursor-pointer',
        'bg-usfGreen text-slate-100 disabled:bg-slate-600',
        !addBtnDisabled ? 'transform hover:scale-105' : ''
    )

    const deleteLinkBtnClasses = classNames(
        'px-2 py-1 rounded-md mr-3 cursor-pointer',
        'bg-red-700 text-slate-100 disabled:bg-slate-600',
        !deleteBtnDisabled ? 'transform hover:scale-105' : ''
    )

    useEffect(() => {
        const handleOutsideClick = (e: MouseEvent) => {
            if (e.target === null) return

            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false)
            }
        }

        document.addEventListener('mousedown', handleOutsideClick)

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick)
        }
    }, [])

    useEffect(() => {
        if (linkTitle === '' || linkUrl === '') {
            setAddBtnDisabled(true)
        } else {
            setAddBtnDisabled(false)
        }
    }, [linkTitle, linkUrl])

    const handleLinkBtnClick = (e: React.MouseEvent) => {
        // Opening the modal
        e.preventDefault()

        const { link, selectedText } = getCurrentValues()

        setLinkUrl((link && link.url) || '')
        setLinkTitle((link && link.title) || selectedText)

        setOpen(!open)
    }

    const handleLinkTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault()
        setLinkTitle(e.target.value)
    }

    const handleLinkUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault()
        setLinkUrl(e.target.value)
    }

    const getCurrentValues = () => {
        const contentState = editorState.getCurrentContent()

        const currentValues: LinkEditorValues = {
            link: {
                url: '',
                title: '',
            },
            selectedText: '',
        }

        if (currentEntity && contentState.getEntity(currentEntity).getType() === 'LINK') {
            const entityRange = getEntityRange(editorState, currentEntity)

            const { url } = contentState.getEntity(currentEntity).getData()

            currentValues.link.url = url
            currentValues.link.title = entityRange!.text
        }
        currentValues.selectedText = getSelectedText(editorState)
        return currentValues
    }

    const onAddLink = (e: React.MouseEvent) => {
        e.preventDefault()

        let selection = editorState.getSelection()

        if (currentEntity) {
            const entityRange = getEntityRange(editorState, currentEntity) // If someone selects only part of a link/focuses cursor on existing link, edit/delete the entire link
            const isBackward = selection.getIsBackward()

            if (isBackward) {
                selection = selection.merge({
                    anchorOffset: entityRange!.end,
                    focusOffset: entityRange!.start,
                })
            } else {
                selection = selection.merge({
                    anchorOffset: entityRange!.start,
                    focusOffset: entityRange!.end,
                })
            }
        }

        const entityKey = editorState
            .getCurrentContent()
            .createEntity('LINK', 'MUTABLE', {
                url: linkUrl,
            })
            .getLastCreatedEntityKey()

        let contentState = Modifier.replaceText(editorState.getCurrentContent(), selection, linkTitle, editorState.getCurrentInlineStyle(), entityKey)

        let newEditorState = EditorState.push(editorState, contentState, 'insert-characters')

        // Insert blank space after the link to move cursor to the right spot (only if adding a new link and not editing existing one)
        if (!currentEntity) {
            selection = newEditorState.getSelection().merge({
                anchorOffset: selection.getAnchorOffset() + linkTitle.length,
                focusOffset: selection.getAnchorOffset() + linkTitle.length,
            })

            newEditorState = EditorState.acceptSelection(newEditorState, selection)

            contentState = Modifier.insertText(newEditorState.getCurrentContent(), selection, ' ', newEditorState.getCurrentInlineStyle(), undefined)

            newEditorState = EditorState.push(newEditorState, contentState, 'insert-characters')
        }

        // Move the cursor after the link
        newEditorState = EditorState.forceSelection(newEditorState, contentState.getSelectionAfter())

        setEditorState(newEditorState)
        setOpen(false)
    }

    const onDeleteLink = (e: React.MouseEvent) => {
        e.preventDefault()

        let selection = editorState.getSelection()

        if (currentEntity) {
            const entityRange = getEntityRange(editorState, currentEntity)
            const isBackward = selection.getIsBackward()

            if (isBackward) {
                selection = selection.merge({
                    anchorOffset: entityRange!.end,
                    focusOffset: entityRange!.start,
                })
            } else {
                selection = selection.merge({
                    anchorOffset: entityRange!.start,
                    focusOffset: entityRange!.end,
                })
            }
            setEditorState(RichUtils.toggleLink(editorState, selection, null))
            setOpen(false)
        }
    }

    return (
        // Did not use <Button></Button> component because these buttons are styled very particularly for the RichTextDropdown component
        <div className="inline-block relative mr-1" ref={ref}>
            <button type="button" className={buttonClasses} onMouseDown={handleLinkBtnClick}>
                <span className="pr-1 font-bold flex-1">
                    <Link45deg size={24} />
                </span>
                <span>
                    <svg
                        className={`fill-current h-4 w-4 transform transition ${open && '-rotate-180'} duration-300 ease-in-out`}
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                    >
                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                </span>
            </button>
            {open && (
                <div className={dropdownClasses}>
                    <div className="mb-2">
                        <label className="block mb-1">Link Title</label>
                        <input className={linkInputClasses} type="text" value={linkTitle} onChange={handleLinkTitleChange} />
                    </div>
                    <div className="mb-2">
                        <label className="block mb-1">Link URL</label>
                        <input className={linkInputClasses} type="text" value={linkUrl} onChange={handleLinkUrlChange} />
                    </div>
                    <div className="text-center mt-4">
                        <Button
                            variant="default"
                            disabled={addBtnDisabled}
                            onMouseDown={onAddLink}
                            abbr={addBtnDisabled ? "Button is disabled" : "Add URL to text"}
                        >
                            Add
                        </Button>
                        <Button
                            variant="destructive"
                            disabled={deleteBtnDisabled}
                            onMouseDown={onDeleteLink}
                            abbr={deleteBtnDisabled ? "Button is disabled" : "Detach URL from text"}
                        >
                            Delete
                        </Button>
                    </div>
                </div>
            )}
        </div>
    )
}
